import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import i18n, { resolveBrowserLanguage, PERSIST_STORAGE_KEY } from '../i18n/index.js';
import { filterOffersForStock } from '../utils/stockUtils.js';

export function getDefaultForm(config = null) {
  // Offers containing out-of-stock toppings are hidden and can never seed a form.
  const offers = filterOffersForStock(config?.offers, config);
  const firstOffer = offers[0];
  const defaultOffer = firstOffer?.name || '';
  const defaultSelectedToppings = Array.isArray(firstOffer?.toppings) && firstOffer.toppings.length > 0
    ? [...firstOffer.toppings]
    : (Array.isArray(config?.defaultSelectedToppings) && config.defaultSelectedToppings.length > 0
      ? [...config.defaultSelectedToppings]
      : []);

  return {
    customerName: '',
    offer: defaultOffer,
    toppings: [...defaultSelectedToppings],
    customToppings: '',
    comment: '',
    half: false,
    menuTab: 'specials',
  };
}

export function getHydratedForm(config = null, storedForm = null) {
  const baseForm = getDefaultForm(config);
  if (!storedForm) {
    return baseForm;
  }

  // A stored offer that is hidden (out of stock) falls back to the default.
  const offerExists = filterOffersForStock(config?.offers, config).some((offer) => offer.name === storedForm.offer);

  return {
    ...baseForm,
    ...storedForm,
    customerName: storedForm.customerName || baseForm.customerName,
    offer: offerExists ? storedForm.offer : baseForm.offer,
    toppings: Array.isArray(storedForm.toppings) && storedForm.toppings.length > 0 ? storedForm.toppings : baseForm.toppings,
    customToppings: storedForm.customToppings ?? '',
    comment: storedForm.comment ?? '',
    half: !!storedForm.half,
    menuTab: storedForm.menuTab || 'specials',
  };
}

function applyThemeMode(themeMode) {
  if (typeof document === 'undefined') {
    return;
  }

  const root = document.documentElement;
  const resolvedTheme = themeMode === 'system'
    ? window.matchMedia('(prefers-color-scheme: dark)').matches ? 'night' : 'day'
    : themeMode;

  root.dataset.theme = resolvedTheme;
  root.classList.toggle('dark', resolvedTheme === 'night');
  root.style.colorScheme = resolvedTheme === 'night' ? 'dark' : 'light';
}

function applyFontSize(fontSize) {
  if (typeof document !== 'undefined') {
    document.documentElement.style.fontSize = fontSize === 125 ? '125%' : '';
  }
}

function createStorage() {
  if (typeof window === 'undefined') {
    return {
      getItem: () => null,
      setItem: () => undefined,
      removeItem: () => undefined,
    };
  }
  return createJSONStorage(() => window.localStorage);
}

export const useSettingsStore = create(
  persist(
    (set) => ({
      currentCustomer: null,
      form: getDefaultForm(null),
      mode: 'menu',
      themeMode: 'system',
      fontSize: 100,
      language: resolveBrowserLanguage(),
      bakerPin: '',
      pinValidated: false,
      includePinInShare: false,
      sessionId: '',
      sessionValidated: false,
      ownerTokens: {},
      setMode: (mode) => set({ mode }),
      setThemeMode: (themeMode) => {
        applyThemeMode(themeMode);
        set({ themeMode });
      },
      setFontSize: (fontSize) => {
        applyFontSize(fontSize);
        set({ fontSize });
      },
      setLanguage: (language) => {
        i18n.changeLanguage(language);
        set({ language });
      },
      setForm: (nextForm) =>
        set((state) => {
          const form = typeof nextForm === 'function' ? nextForm(state.form) : nextForm;
          return { form };
        }),
      setBakerPin: (bakerPin) => set({ bakerPin }),
      setPinValidated: (pinValidated) => set({ pinValidated }),
      setIncludePinInShare: (includePinInShare) => set({ includePinInShare }),
      setSessionId: (sessionId) => set({ sessionId }),
      setSessionValidated: (sessionValidated) => set({ sessionValidated }),
    }),
    {
      name: PERSIST_STORAGE_KEY,
      storage: createStorage(),
    },
  ),
);

applyThemeMode(useSettingsStore.getState().themeMode);
applyFontSize(useSettingsStore.getState().fontSize);

if (typeof window !== 'undefined') {
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  const handleSystemThemeChange = () => {
    if (useSettingsStore.getState().themeMode === 'system') {
      applyThemeMode('system');
    }
  };

  if (typeof mediaQuery.addEventListener === 'function') {
    mediaQuery.addEventListener('change', handleSystemThemeChange);
  } else {
    mediaQuery.addListener(handleSystemThemeChange);
  }
}
